import { EVENT_USER_LANGUAGE_CHANGED, EVENT_USER_PROFILE_CHANGED } from '@rio-cloud/rio-user-menu-component';
import type { UserManager } from 'oidc-client-ts';

import Logger from '@/utils/logger';

import { config } from '../config';
import { reduxStore } from '../store/store';
import { configureFetchDisplayMessages } from './lang/services';
import {
  configureMockUserManager,
  configureUserManager,
  createUserManager,
  type SessionRenewedResult,
} from './login/login';
import { userProfileObtained, userSessionExpired, userSessionRenewed } from './login/loginSlice';
import { attemptInitialSignIn } from './setup/oauth';
import { reportErrorToSentry } from './setup/sentry';
import { accessToken } from './tokenHandling/accessToken';
import { accessTokenStored, idTokenStored } from './tokenHandling/tokenSlice';

const LOGGER_TAG = 'Configuration';

export interface OAuthConfig {
  onSessionExpired: () => void;
  onSessionRenewed: (result: SessionRenewedResult) => void;
}

export const main = async (renderApp: () => void) => {
  const fetchDisplayMessages = configureFetchDisplayMessages(reduxStore);

  const oauthConfig = {
    onSessionExpired: () => {
      accessToken.discardAccessToken();
      reduxStore.dispatch(userSessionExpired());
    },
    onSessionRenewed: (result: SessionRenewedResult) => {
      Logger.trace(LOGGER_TAG, 'Session renewed', result);

      if(result.accessToken) {
        accessToken.saveAccessToken(result.accessToken);
      }

      reduxStore.dispatch(accessTokenStored(result.accessToken));
      reduxStore.dispatch(idTokenStored(result.idToken));
      reduxStore.dispatch(userProfileObtained(result.profile));

      reduxStore.dispatch(userSessionRenewed());

      // You will need to get the user language yourself then
      // you may fetch the suitable messages. Depending
      // on when and from where you fetch the user settings you might
      // want to employ a loading spinner while the request is ongoing.
      fetchDisplayMessages(result.locale);
    },
  } as OAuthConfig;

  const userManager: UserManager = config.login.mockAuthorization
    ? configureMockUserManager(oauthConfig)
    : configureUserManager(oauthConfig, createUserManager());

  const signinSilent = userManager.signinSilent.bind(userManager);
  document.addEventListener(EVENT_USER_LANGUAGE_CHANGED, () => signinSilent());
  document.addEventListener(EVENT_USER_PROFILE_CHANGED, () => signinSilent());

  try {
    await userManager.clearStaleState();
    await attemptInitialSignIn(userManager);
    renderApp();
  } catch (error) {
    Logger.trace(LOGGER_TAG, 'could not start application', error);
    reportErrorToSentry(error);
  }
};
